/**
 * On-time delivery badge widget (Sprint 90).
 *
 * Small inline card that fetches /api/uptime-badge.json and shows the
 * headline on-time % + sample size. Renders nothing when sample is too
 * small to be meaningful (< 5 deliveries) — avoids embarrassing
 * "On-time 100% (1 sample)" early-stage flexes.
 */
import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import api from "../services/api";

export default function OnTimeBadge() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/uptime-badge.json")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data?.headline_pct) return null;

  const pct = data.headline_pct;
  const window = data.headline_window;
  const w = data.windows?.[window];
  const sample = w?.sample_size || 0;
  if (sample < 5) return null;

  // Color tier
  const tier =
    pct >= 95 ? { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500" }
    : pct >= 85 ? { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", dot: "bg-amber-500" }
    : { bg: "bg-red-50", text: "text-red-700", ring: "ring-red-200", dot: "bg-red-500" };

  return (
    <a
      href="/status"
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${tier.bg} ${tier.text} ring-1 ${tier.ring} no-underline hover:shadow-sm transition-shadow`}
      title={`On-time delivery — ${sample} parcels in last ${window}. Click to see system status.`}
      data-testid="on-time-badge"
    >
      <span className={`h-1.5 w-1.5 rounded-full ${tier.dot}`} />
      <CheckCircle2 className="h-3.5 w-3.5" />
      <span>On-time {pct}% · {window}</span>
    </a>
  );
}
